import React, { useState, useEffect } from 'react';
import BrandingEditor from './BrandingEditor';
import WebsiteVisualizer from './WebsiteVisualizer';
import AboutUsEditor from './AboutUsEditor';
import ContactUsEditor from './ContactUsEditor';
import FaqEditor from './FaqEditor';
import apiService from './Common/Configurations/APIService';
import TermsEditor from './TermsEditor';
import PrivacyPolicyEditor from './PrivacyPolicyEditor';
import RefundPolicyEditor from './RefundPolicyEditor';
import config from './Common/Configurations/APIConfig';
import { Spinner } from 'react-bootstrap';
import { ToastContainer } from 'react-toastify';

const WebsiteManager = () => {
    const [step, setStep] = useState(1); // 1: Branding, 2: Style
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const safeJSON = (str, fallback = {}) => {
        if (!str || str === 'string' || str === 'null') return fallback;
        try { return JSON.parse(str); } catch { return fallback; }
    };

    useEffect(() => {
        const fetchCurrentConfig = async () => {
            try {
                // ⭐ Capture manageDomain from URL (passed from Admin project)
                const params = new URLSearchParams(window.location.search);
                const manageDomain = params.get('manageDomain');
                
                const host = window.location.hostname.toLowerCase();
                const activeDomain = manageDomain || (host === 'localhost' ? 'epicdestino.in' : host);

                const res = await apiService.getAll(`${config.GETTEMPLATEBYDOMAIN}/${activeDomain}`);
                const d = res.data || res;

                // Parse everything into objects for the editors
                setData({
                    companyTemplateId: d.companyTemplateId,
                    companyId:         d.companyId,
                    branchId:          d.branchId,
                    userId:            d.userId,
                    templateId:        d.templateId,
                    templateType:      d.templateType,
                    templateVersionId: d.templateVersionId,
                    templateReference: d.templateReference || d.templateRefference, // Fix potential spelling mismatch
                    domain:            d.domain,
                    assignedAt:        d.assignedAt,
                    // Parsed JSONs for Editor
                    branding:      safeJSON(d.customBrandingJson, {}),
                    theme:         safeJSON(d.styleJson, {}),
                    topBar:        safeJSON(d.topBarJson, {}),
                    header:        safeJSON(d.headerJson, []),
                    hero:          safeJSON(d.heroJson, {}),
                    footer:        safeJSON(d.footerJson, {}),
                    aboutUs:       safeJSON(d.aboutUsJson, {}),
                    contactUs:     safeJSON(d.contactUsJson, {}),
                    faq: safeJSON(d.faqJson, {}),
                    terms: safeJSON(d.termsAndConditionsJson, {}),
                    privacyPolicy: safeJSON(d.privacyPolicyJson, {}),
                    refundPolicy: safeJSON(d.refundPolicyJson, {}),
                    seo:           safeJSON(d.seoJson, {}),
                    destinations:  safeJSON(d.destinationsJson, { destinations: [] }).destinations || [],
                    // Preserve raw strings for fields we aren't editing yet
                    layoutJson:    d.layoutJson,
                    activeModulesJson: d.activeModulesJson
                });
            } catch (e) { 
                console.error("Failed to load website config", e); 
            } finally { 
                setLoading(false); 
            }
        };
        fetchCurrentConfig();
    }, []);
 const handleSaveAboutUs = async (aboutUsData) => {
  if (!aboutUsData) {
    console.error("handleSaveAboutUs called with no data!");
    return;
  }
  setIsSaving(true);
  try {
    const formData = new FormData();
    formData.append("CompanyId", data.companyId);
    formData.append("BranchId", data.branchId);
    formData.append("UserId", data.userId);
    formData.append("TemplateId", data.templateId);
    formData.append("TemplateType", data.templateType);
    formData.append("TemplateReference", data.templateReference);
    formData.append("Domain", data.domain);
    formData.append("CustomBrandingJson", JSON.stringify(data.branding));
    formData.append("StyleJson", JSON.stringify(data.theme));
    formData.append("TopBarJson", JSON.stringify(data.topBar));
    formData.append("HeaderJson", JSON.stringify(data.header));
    formData.append("HeroJson", JSON.stringify(data.hero));
    formData.append("FooterJson", JSON.stringify(data.footer));
    formData.append("AboutUsJson", JSON.stringify(aboutUsData));  // ← fresh from editor
    formData.append("ContactUsJson", JSON.stringify(data.contactUs));
    formData.append("FaqJson", JSON.stringify(data.faq));
    formData.append("TermsAndConditionsJson", JSON.stringify(data.terms));
    formData.append("PrivacyPolicyJson", JSON.stringify(data.privacyPolicy));
    formData.append("RefundPolicyJson", JSON.stringify(data.refundPolicy));
    formData.append("SeoJson", JSON.stringify(data.seo));
    formData.append("DestinationsJson", JSON.stringify({ destinations: data.destinations }));

    await apiService.post(config.ASSIGNTEMPLATE, formData);

    // Also update parent data so subsequent saves on Step 1 don't overwrite with stale aboutUs
    setData(prev => ({ ...prev, aboutUs: aboutUsData }));
  } catch (e) {
    console.error("Save failed:", e);
  } finally {
    setIsSaving(false);
  }
};
const handleSaveContactUs = async (contactUsData) => {
  if (!contactUsData) return;
  setIsSaving(true);
  try {
    const formData = new FormData();
    formData.append("CompanyId",          data.companyId);
    formData.append("BranchId",           data.branchId);
    formData.append("UserId",             data.userId);
    formData.append("TemplateId",         data.templateId);
    formData.append("TemplateType",       data.templateType);
    formData.append("TemplateReference",  data.templateReference);
    formData.append("Domain",             data.domain);
    formData.append("CustomBrandingJson", JSON.stringify(data.branding));
    formData.append("StyleJson",          JSON.stringify(data.theme));
    formData.append("TopBarJson",         JSON.stringify(data.topBar));
    formData.append("HeaderJson",         JSON.stringify(data.header));
    formData.append("HeroJson",           JSON.stringify(data.hero));
    formData.append("FooterJson",         JSON.stringify(data.footer));
    formData.append("AboutUsJson",        JSON.stringify(data.aboutUs));
    formData.append("ContactUsJson",      JSON.stringify(contactUsData)); // ← fresh
    formData.append("FaqJson",            JSON.stringify(data.faq));
    formData.append("TermsAndConditionsJson",          JSON.stringify(data.terms));
    formData.append("PrivacyPolicyJson", JSON.stringify(data.privacyPolicy));
    formData.append("RefundPolicyJson", JSON.stringify(data.refundPolicy));
    formData.append("SeoJson",            JSON.stringify(data.seo));
    formData.append("DestinationsJson",   JSON.stringify({ destinations: data.destinations }));

    await apiService.post(config.ASSIGNTEMPLATE, formData);
    setData(prev => ({ ...prev, contactUs: contactUsData }));
  } catch (e) {
    
    console.error(e);
  } finally {
    setIsSaving(false);
  }
};
const handleSaveFaq = async (faqData) => {
  if (!faqData) return;
  setIsSaving(true);
  try {
    const formData = new FormData();
    formData.append("CompanyId",          data.companyId);
    formData.append("BranchId",           data.branchId);
    formData.append("UserId",             data.userId);
    formData.append("TemplateId",         data.templateId);
    formData.append("TemplateType",       data.templateType);
    formData.append("TemplateReference",  data.templateReference);
    formData.append("Domain",             data.domain);
    formData.append("CustomBrandingJson", JSON.stringify(data.branding));
    formData.append("StyleJson",          JSON.stringify(data.theme));
    formData.append("TopBarJson",         JSON.stringify(data.topBar));
    formData.append("HeaderJson",         JSON.stringify(data.header));
    formData.append("HeroJson",           JSON.stringify(data.hero));
    formData.append("FooterJson",         JSON.stringify(data.footer));
    formData.append("AboutUsJson",        JSON.stringify(data.aboutUs));
    formData.append("ContactUsJson",      JSON.stringify(data.contactUs));
    formData.append("TermsAndConditionsJson",          JSON.stringify(data.terms));
    formData.append("RefundPolicyJson", JSON.stringify(data.refundPolicy));
    formData.append("SeoJson",            JSON.stringify(data.seo));
    formData.append("DestinationsJson",   JSON.stringify({ destinations: data.destinations }));
    formData.append("FaqJson",            JSON.stringify(faqData)); // ← new field
    formData.append("PrivacyPolicyJson", JSON.stringify(data.privacyPolicy)); // ← new field

    await apiService.post(config.ASSIGNTEMPLATE, formData);
    setData(prev => ({ ...prev, faq: faqData }));
  } catch (e) {
   
    console.error(e);
  } finally {
    setIsSaving(false);
  }
};
const handleSaveTerms = async (termsData) => {
  if (!termsData) return;
  setIsSaving(true);
  try {
    const formData = new FormData();
    formData.append("CompanyId",          data.companyId);
    formData.append("BranchId",           data.branchId);
    formData.append("UserId",             data.userId);
    formData.append("TemplateId",         data.templateId);
    formData.append("TemplateType",       data.templateType);
    formData.append("TemplateReference",  data.templateReference);
    formData.append("Domain",             data.domain);
    formData.append("CustomBrandingJson", JSON.stringify(data.branding));
    formData.append("StyleJson",          JSON.stringify(data.theme));
    formData.append("TopBarJson",         JSON.stringify(data.topBar));
    formData.append("HeaderJson",         JSON.stringify(data.header));
    formData.append("HeroJson",           JSON.stringify(data.hero));
    formData.append("FooterJson",         JSON.stringify(data.footer));
    formData.append("AboutUsJson",        JSON.stringify(data.aboutUs));
    formData.append("ContactUsJson",      JSON.stringify(data.contactUs));
    formData.append("FaqJson",            JSON.stringify(data.faq));
    formData.append("TermsAndConditionsJson",          JSON.stringify(termsData)); // ← new field
    formData.append("RefundPolicyJson", JSON.stringify(data.refundPolicy));
    formData.append("SeoJson",            JSON.stringify(data.seo));
    formData.append("DestinationsJson",   JSON.stringify({ destinations: data.destinations }));
        formData.append("PrivacyPolicyJson", JSON.stringify(data.privacyPolicy)); // ← new field
   
    await apiService.post(config.ASSIGNTEMPLATE, formData);
    setData(prev => ({ ...prev, terms: termsData }));
  } catch (e) {

    console.error(e);
  } finally {
    setIsSaving(false);
  }
};
const handleSavePrivacyPolicy = async (privacyPolicyData) => {
  if (!privacyPolicyData) return;
  setIsSaving(true);
  try {
    const formData = new FormData();
    formData.append("CompanyId",            data.companyId);
    formData.append("BranchId",             data.branchId);
    formData.append("UserId",               data.userId);
    formData.append("TemplateId",           data.templateId);
    formData.append("TemplateType",         data.templateType);
    formData.append("TemplateReference",    data.templateReference);
    formData.append("Domain",               data.domain);
    formData.append("CustomBrandingJson",   JSON.stringify(data.branding));
    formData.append("StyleJson",            JSON.stringify(data.theme));
    formData.append("TopBarJson",           JSON.stringify(data.topBar));
    formData.append("HeaderJson",           JSON.stringify(data.header));
    formData.append("HeroJson",             JSON.stringify(data.hero));
    formData.append("FooterJson",           JSON.stringify(data.footer));
    formData.append("AboutUsJson",          JSON.stringify(data.aboutUs));
    formData.append("ContactUsJson",        JSON.stringify(data.contactUs));
    formData.append("FaqJson",              JSON.stringify(data.faq));
    formData.append("TermsAndConditionsJson", JSON.stringify(data.terms));
    formData.append("RefundPolicyJson", JSON.stringify(data.refundPolicy));
    formData.append("PrivacyPolicyJson",    JSON.stringify(privacyPolicyData)); // ← fresh
    formData.append("SeoJson",              JSON.stringify(data.seo));
    formData.append("DestinationsJson",     JSON.stringify({ destinations: data.destinations }));
    formData.append("TermsAndConditionsJson", JSON.stringify(data.terms));
 
    await apiService.post(config.ASSIGNTEMPLATE, formData);
    setData(prev => ({ ...prev, privacyPolicy: privacyPolicyData }));
  } catch (e) {
    console.error(e);
  } finally {
    setIsSaving(false);
  }
};
const handleSaveRefundPolicy = async (refundPolicyData) => {
  if (!refundPolicyData) return;
  setIsSaving(true);    
    try {
        const formData = new FormData();
        formData.append("CompanyId",            data.companyId);
    formData.append("BranchId",             data.branchId);
    formData.append("UserId",               data.userId);
    formData.append("TemplateId",           data.templateId);
    formData.append("TemplateType",         data.templateType);
    formData.append("TemplateReference",    data.templateReference);
    formData.append("Domain",               data.domain);
    formData.append("CustomBrandingJson",   JSON.stringify(data.branding));
    formData.append("StyleJson",            JSON.stringify(data.theme));
    formData.append("TopBarJson",           JSON.stringify(data.topBar));
    formData.append("HeaderJson",           JSON.stringify(data.header));
    formData.append("HeroJson",             JSON.stringify(data.hero));
    formData.append("FooterJson",           JSON.stringify(data.footer));
    formData.append("AboutUsJson",          JSON.stringify(data.aboutUs));
    formData.append("ContactUsJson",        JSON.stringify(data.contactUs));
    formData.append("FaqJson",              JSON.stringify(data.faq));
    formData.append("TermsAndConditionsJson", JSON.stringify(data.terms));
   formData.append("RefundPolicyJson", JSON.stringify(refundPolicyData)); // ← fresh
    formData.append("PrivacyPolicyJson", JSON.stringify(data.privacyPolicy));
    formData.append("SeoJson",              JSON.stringify(data.seo));
    formData.append("DestinationsJson",     JSON.stringify({ destinations: data.destinations }));
    formData.append("TermsAndConditionsJson", JSON.stringify(data.terms));
 
    await apiService.post(config.ASSIGNTEMPLATE, formData);
    setData(prev => ({ ...prev, refundPolicy: refundPolicyData }));
  } catch (e) {
    console.error(e);
  } finally {
    setIsSaving(false);
  }
};

  
    if (loading) return (
        <div className="vh-100 d-flex flex-column justify-content-center align-items-center bg-light">
            <Spinner animation="border" variant="primary" />
            <h5 className="mt-3 fw-bold text-primary">Loading Website Manager...</h5>
        </div>
    );

    return (
        <div className="website-manager-wrapper" style={{ minHeight: '100vh', background: '#f4f7fa' }}>
            <ToastContainer />
            
            {/* Top Navigation Step Bar */}
            <div style={{ background: '#1a1a1a', padding: '15px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', position: 'sticky', top: 0, zIndex: 1000 }}>
                <div className="d-flex gap-4">
                    <div 
                        onClick={() => setStep(1)}
                        style={{ cursor: 'pointer', opacity: step === 1 ? 1 : 0.5, borderBottom: step === 1 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600, transition: '0.3s' }}
                    >
                        <span className="badge bg-primary me-2">1</span> Branding & Identity
                    </div>
                    <div 
                        onClick={() => setStep(2)}
                        style={{ cursor: 'pointer', opacity: step === 2 ? 1 : 0.5, borderBottom: step === 2 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600, transition: '0.3s' }}
                    >
                        <span className="badge bg-primary me-2">2</span> Styles & Visuals
                    </div>
                    <div onClick={() => setStep(3)}
  style={{ cursor: 'pointer', opacity: step === 3 ? 1 : 0.5, borderBottom: step === 3 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600 }}>
  <span className="badge bg-primary me-2">3</span> About Us
</div>
<div onClick={() => setStep(4)}
  style={{ cursor: 'pointer', opacity: step === 4 ? 1 : 0.5, borderBottom: step === 4 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600 }}>
  <span className="badge bg-primary me-2">4</span> Contact Us
</div>
<div onClick={() => setStep(5)}
  style={{ cursor: 'pointer', opacity: step === 5 ? 1 : 0.5, borderBottom: step === 5 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600 }}>
  <span className="badge bg-primary me-2">5</span> FAQ
</div>
<div onClick={() => setStep(6)}
  style={{ cursor: 'pointer', opacity: step === 6 ? 1 : 0.5, borderBottom: step === 6 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600 }}>
  <span className="badge bg-primary me-2">6</span> Terms
</div>
<div
  onClick={() => setStep(7)}
  style={{ cursor: 'pointer', opacity: step === 7 ? 1 : 0.5, borderBottom: step === 7 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600 }}
>
  <span className="badge bg-primary me-2">7</span> Privacy Policy
</div>
<div onClick={() => setStep(8)}
  style={{ cursor: 'pointer', opacity: step === 8 ? 1 : 0.5, borderBottom: step === 8 ? '3px solid #007bff' : 'none', paddingBottom: '5px', fontWeight: 600 }}>
  <span className="badge bg-primary me-2">8</span> Refund Policy
</div>
                </div>
                <div className="d-none d-md-block">
                    <span className="badge bg-dark border border-secondary px-3 py-2">Editing Domain: {data?.domain}</span>
                </div>
            </div>

            {/* Content Rendering */}
            <div className="manager-content">
                {step === 1 && (
                    <BrandingEditor initialData={data} onNext={(updatedData) => { setData(updatedData); setStep(2); }} />
                )}
                {step === 2 && (
  <WebsiteVisualizer
   preloadedData={data} onBack={() => setStep(1)} />
)}
                {step === 3 && (
  <AboutUsEditor
    workingData={data}
    updateSection={(key, val) => setData(prev => ({ ...prev, [key]: val }))}
    onSave={handleSaveAboutUs}
    isSaving={isSaving}
  />
)}
{step === 4 && (
  <ContactUsEditor
    workingData={data}
    updateSection={(key, val) => setData(prev => ({ ...prev, [key]: val }))}
    onSave={handleSaveContactUs}
    isSaving={isSaving}
  />
)}
{step === 5 && (
  <FaqEditor
    workingData={data}
    updateSection={(key, val) => setData(prev => ({ ...prev, [key]: val }))}
    onSave={handleSaveFaq}
    isSaving={isSaving}
  />
)}
{step === 6 && (
  <TermsEditor
    workingData={data}
    updateSection={(key, val) => setData(prev => ({ ...prev, [key]: val }))}
    onSave={handleSaveTerms}
    isSaving={isSaving}
  />
)}
{step === 7 && (
  <PrivacyPolicyEditor
    workingData={data}
    updateSection={(key, val) => setData(prev => ({ ...prev, [key]: val }))}
    onSave={handleSavePrivacyPolicy}
    isSaving={isSaving}
  />
)}
{ step === 8 && (
  <RefundPolicyEditor
    workingData={data}
    updateSection={(key, val) => setData(prev => ({ ...prev, [key]: val }))}
    onSave={handleSaveRefundPolicy}
    isSaving={isSaving}
  />
)}
            </div>
        </div>
    );
};

export default WebsiteManager;